import React, { useState, useEffect, useCallback } from 'react';
import { getExpenses, getReceiptImage } from '../services/api';
import { formatDateDisplay } from '../utils/date';
import { formatNumber } from '../utils/numberFormat';

function ReceiptCard({ expense, onClick }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageLoading(true);
    setImageError(false);

    getReceiptImage(expense.id)
      .then(({ imageUrl: url }) => {
        if (!cancelled) {
          setImageUrl(url);
          setImageLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImageError(true);
          setImageLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [expense.id]);

  const receiptName = [
    expense.vendor_name || 'Unknown',
    `${formatNumber(expense.amount)} ${expense.currency_code || ''}`.trim(),
    formatDateDisplay(expense.date),
  ].join(' - ');

  return (
    <div
      onClick={() => onClick(expense, imageUrl)}
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid #E8E8E8',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Receipt thumbnail */}
      <div style={{
        width: '100%',
        aspectRatio: '3/4',
        backgroundColor: '#F5F5F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {imageLoading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              border: '3px solid #E8E8E8',
              borderTopColor: '#3B82F6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
        {!imageLoading && imageError && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            color: '#A1A1AA',
          }}>
            <span style={{ fontSize: '32px' }}>🧾</span>
            <span style={{ fontSize: '12px' }}>No preview</span>
          </div>
        )}
        {!imageLoading && !imageError && imageUrl && (
          <img
            src={imageUrl}
            alt={receiptName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setImageError(true)}
          />
        )}
      </div>

      {/* Receipt label */}
      <div style={{
        padding: '10px 12px',
        borderTop: '1px solid #F5F5F5',
      }}>
        <p style={{
          margin: 0,
          fontSize: '12px',
          fontWeight: '600',
          color: '#111827',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }} title={receiptName}>
          {expense.vendor_name || 'Unknown'}
        </p>
        <p style={{
          margin: '2px 0 0',
          fontSize: '11px',
          color: '#71717A',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {formatNumber(expense.amount)} {expense.currency_code} · {formatDateDisplay(expense.date)}
        </p>
      </div>
    </div>
  );
}

function ReceiptListRow({ expense, index, onClick }) {
  const [hovered, setHovered] = useState(false);

  const baseAmount = expense.amount_in_default_currency ?? expense.amount ?? 0;
  const displayAmount = formatNumber(baseAmount);

  return (
    <div
      onClick={() => onClick(expense, null)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        gap: '14px',
        cursor: 'pointer',
        backgroundColor: hovered ? '#EFF6FF' : (index % 2 === 0 ? '#FFFFFF' : '#FAFAFA'),
        borderBottom: '1px solid #F5F5F5',
        transition: 'background-color 0.1s ease',
      }}
    >
      {/* Receipt icon */}
      <span style={{ fontSize: '20px', flexShrink: 0, width: '28px', textAlign: 'center' }}>🧾</span>

      {/* Name column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          fontWeight: '600',
          color: '#111827',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {expense.vendor_name || 'Unknown'}
        </p>
        {expense.category && (
          <p style={{
            margin: '2px 0 0',
            fontSize: '11px',
            color: '#71717A',
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {expense.category}
          </p>
        )}
      </div>

      {/* Amount column */}
      <div style={{
        width: '120px',
        flexShrink: 0,
        textAlign: 'right',
      }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          fontWeight: '600',
          color: '#111827',
          whiteSpace: 'nowrap',
        }}>
          {displayAmount}
        </p>
      </div>

      {/* Date Added column */}
      <div style={{
        width: '110px',
        flexShrink: 0,
        textAlign: 'right',
      }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          color: '#71717A',
          whiteSpace: 'nowrap',
        }}>
          {formatDateDisplay(expense.created_at || expense.date)}
        </p>
      </div>
    </div>
  );
}

function ReceiptModal({ expense, imageUrl, onClose }) {
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [loadingFresh, setLoadingFresh] = useState(!imageUrl);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    if (!imageUrl) {
      setLoadingFresh(true);
      getReceiptImage(expense.id)
        .then(({ imageUrl: url }) => {
          setCurrentImageUrl(url);
          setLoadingFresh(false);
        })
        .catch(() => setLoadingFresh(false));
    }
  }, [expense.id, imageUrl]);

  const receiptName = [
    expense.vendor_name || 'Unknown',
    `${formatNumber(expense.amount)} ${expense.currency_code || ''}`.trim(),
    formatDateDisplay(expense.date),
  ].join(' - ');

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          overflow: 'hidden',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E8E8E8',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#111827',
              wordBreak: 'break-word',
              letterSpacing: '-0.025em',
            }}>
              {receiptName}
            </p>
            {expense.category && (
              <span style={{
                display: 'inline-block',
                marginTop: '4px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#3B82F6',
                backgroundColor: '#EFF6FF',
                padding: '2px 8px',
                borderRadius: '99px',
                textTransform: 'capitalize',
              }}>
                {expense.category}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              backgroundColor: '#F5F5F5',
              borderRadius: '10px',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              fontSize: '16px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#374151',
            }}
          >
            ✕
          </button>
        </div>

        {/* Receipt image */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#FAFAFA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
        }}>
          {loadingFresh && (
            <div style={{
              width: '36px',
              height: '36px',
              border: '4px solid #E8E8E8',
              borderTopColor: '#3B82F6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          )}
          {!loadingFresh && currentImageUrl && (
            <img
              src={currentImageUrl}
              alt={receiptName}
              style={{
                maxWidth: '100%',
                display: 'block',
              }}
            />
          )}
          {!loadingFresh && !currentImageUrl && (
            <div style={{ color: '#A1A1AA', textAlign: 'center' }}>
              <span style={{ fontSize: '48px', display: 'block' }}>🧾</span>
              <p style={{ fontSize: '14px', margin: '8px 0 0' }}>Receipt image unavailable</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SortableHeader({ label, columnKey, currentSortBy, currentSortOrder, onSort, align }) {
  const isActive = currentSortBy === columnKey;
  const arrow = isActive ? (currentSortOrder === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div
      onClick={() => onSort(columnKey)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: '11px',
        fontWeight: '600',
        color: isActive ? '#3B82F6' : '#71717A',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        textAlign: align || 'left',
        whiteSpace: 'nowrap',
      }}
    >
      {label}{arrow}
    </div>
  );
}

function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const PAGE_SIZE = 50;

  const fetchReceipts = useCallback(async (currentOffset, append = false, sort = null) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      const sortConfig = sort || { sortBy, sortOrder };

      const result = await getExpenses({
        hasReceipt: true,
        limit: PAGE_SIZE,
        offset: currentOffset,
        sortBy: sortConfig.sortBy,
        sortOrder: sortConfig.sortOrder,
      });

      setTotal(result.total || 0);

      if (append) {
        setReceipts(prev => [...prev, ...result.expenses]);
      } else {
        setReceipts(result.expenses);
      }
    } catch (err) {
      setError(err.message || 'Failed to load receipts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    fetchReceipts(0, false);
  }, [fetchReceipts]);

  const handleLoadMore = () => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    fetchReceipts(newOffset, true);
  };

  const handleCardClick = (expense, imageUrl) => {
    setSelectedReceipt(expense);
    setSelectedImageUrl(imageUrl);
  };

  const handleModalClose = () => {
    setSelectedReceipt(null);
    setSelectedImageUrl(null);
  };

  const handleSort = (columnKey) => {
    let newOrder;
    if (sortBy === columnKey) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOrder = 'desc';
    }
    setSortBy(columnKey);
    setSortOrder(newOrder);
    setOffset(0);
    setReceipts([]);
    fetchReceipts(0, false, { sortBy: columnKey, sortOrder: newOrder });
  };

  // Client-side re-sort for amount_base to handle NULL amount_in_default_currency
  const displayReceipts = sortBy === 'amount_base'
    ? [...receipts].sort((a, b) => {
        const aAmt = a.amount_in_default_currency ?? a.amount ?? 0;
        const bAmt = b.amount_in_default_currency ?? b.amount ?? 0;
        return sortOrder === 'desc' ? bAmt - aAmt : aAmt - bAmt;
      })
    : receipts;

  const hasMore = receipts.length < total;

  return (
    <div style={{
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
    }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 4px',
            letterSpacing: '-0.025em',
          }}>
            🧾 Receipts
          </h1>
          <p style={{ color: '#71717A', fontSize: '14px', margin: 0 }}>
            {loading ? 'Loading…' : `${total} receipt${total !== 1 ? 's' : ''} uploaded`}
          </p>
        </div>

        {/* View mode toggle */}
        {!loading && !error && receipts.length > 0 && (
          <div style={{
            display: 'flex',
            backgroundColor: '#F5F5F5',
            borderRadius: '10px',
            padding: '2px',
            gap: '2px',
          }}>
            <button
              onClick={() => setViewMode('grid')}
              title="Thumbnail view"
              style={{
                border: 'none',
                backgroundColor: viewMode === 'grid' ? '#FFFFFF' : 'transparent',
                borderRadius: '10px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '14px',
                color: viewMode === 'grid' ? '#111827' : '#71717A',
                fontWeight: viewMode === 'grid' ? '600' : '400',
                boxShadow: viewMode === 'grid' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              ⊞
            </button>
            <button
              onClick={() => setViewMode('list')}
              title="List view"
              style={{
                border: 'none',
                backgroundColor: viewMode === 'list' ? '#FFFFFF' : 'transparent',
                borderRadius: '10px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '14px',
                color: viewMode === 'list' ? '#111827' : '#71717A',
                fontWeight: viewMode === 'list' ? '600' : '400',
                boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              ≡
            </button>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          gap: '12px',
          color: '#71717A',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #E8E8E8',
            borderTopColor: '#3B82F6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <span>Loading receipts…</span>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '14px',
          padding: '20px',
          color: '#B91C1C',
          textAlign: 'center',
        }}>
          <p style={{ margin: '0 0 12px', fontWeight: '600' }}>Failed to load receipts</p>
          <p style={{ margin: '0 0 16px', fontSize: '13px' }}>{error}</p>
          <button
            onClick={() => fetchReceipts(0, false)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#EF4444',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '14px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && receipts.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 24px',
          backgroundColor: '#FFFFFF',
          borderRadius: '14px',
          border: '1px solid #E8E8E8',
        }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🧾</span>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px', letterSpacing: '-0.025em' }}>
            No receipts yet
          </h3>
          <p style={{ color: '#71717A', fontSize: '14px', margin: 0 }}>
            Upload a receipt using the Upload Receipt menu to see it here.
          </p>
        </div>
      )}

      {/* Receipts grid view */}
      {!loading && !error && receipts.length > 0 && viewMode === 'grid' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '16px',
          }}>
            {displayReceipts.map((expense) => (
              <ReceiptCard
                key={expense.id}
                expense={expense}
                onClick={handleCardClick}
              />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{
                  padding: '10px 28px',
                  backgroundColor: loadingMore ? '#E8E8E8' : '#3B82F6',
                  color: loadingMore ? '#A1A1AA' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: loadingMore ? 'default' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {loadingMore ? 'Loading…' : `Load more (${total - receipts.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Receipts list view */}
      {!loading && !error && receipts.length > 0 && viewMode === 'list' && (
        <>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '14px',
            border: '1px solid #E8E8E8',
            overflow: 'hidden',
          }}>
            {/* List header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 16px',
              gap: '14px',
              borderBottom: '1px solid #E8E8E8',
              backgroundColor: '#FAFAFA',
            }}>
              {/* Icon spacer */}
              <div style={{ width: '28px', flexShrink: 0 }} />

              {/* Name header */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <SortableHeader
                  label="Name"
                  columnKey="vendor_name"
                  currentSortBy={sortBy}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                />
              </div>

              {/* Amount header */}
              <div style={{ width: '120px', flexShrink: 0 }}>
                <SortableHeader
                  label="Amount (SGD)"
                  columnKey="amount_base"
                  currentSortBy={sortBy}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                  align="right"
                />
              </div>

              {/* Date Added header */}
              <div style={{ width: '110px', flexShrink: 0 }}>
                <SortableHeader
                  label="Date Added"
                  columnKey="created_at"
                  currentSortBy={sortBy}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                  align="right"
                />
              </div>
            </div>

            {/* List rows */}
            {displayReceipts.map((expense, index) => (
              <ReceiptListRow
                key={expense.id}
                expense={expense}
                index={index}
                onClick={handleCardClick}
              />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{
                  padding: '10px 28px',
                  backgroundColor: loadingMore ? '#E8E8E8' : '#3B82F6',
                  color: loadingMore ? '#A1A1AA' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: loadingMore ? 'default' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {loadingMore ? 'Loading…' : `Load more (${total - receipts.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {selectedReceipt && (
        <ReceiptModal
          expense={selectedReceipt}
          imageUrl={selectedImageUrl}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

export default ReceiptsPage;
